import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import isNil from 'lodash/isNil';

import { getAgeFormatted } from 'shared/utils/ageUtils';

import { AVAILABLE, AWAY, UNAVAILABLE } from 'modules/common/constants/userAvailabilityTypes';
import ExpandablePanel from '../ExpandablePanel';
import DetailsInfoRow from '../DetailsInfoRow';

import theme from './CustomerPatientInfoRow.scss';

const propTypes = {
  customer: PropTypes.shape({
    availability: PropTypes.oneOf([AWAY, UNAVAILABLE, AVAILABLE]),
    name: PropTypes.string,
    policyId: PropTypes.string,
    dateOfBirth: PropTypes.string,
    phoneNumber: PropTypes.string,
    initials: PropTypes.string,
    avatar: PropTypes.string,
    email: PropTypes.string,
    gender: PropTypes.shape({
      iconName: PropTypes.string,
      title: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
      }),
    }),
    isPatientInWaitingRoom: PropTypes.bool,
  }),
  permissions: PropTypes.shape({
    canSeeCustomer: PropTypes.bool.isRequired,
  }).isRequired,
  isDisabled: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

const defaultProps = {
  customer: {
    name: null,
    policyId: null,
    gender: null,
    phoneNumber: null,
    dateOfBirth: null,
    email: null,
    avatar: null,
    initials: null,
    availability: null,
    isPatientInWaitingRoom: false,
  },
  isDisabled: false,
};

const CustomerPatientInfoRow = ({
  customer: {
    policyId,
    name,
    gender,
    email,
    dateOfBirth,
    phoneNumber,
    avatar,
    initials,
    availability,
    isPatientInWaitingRoom,
  },
  permissions,
  intl,
  isDisabled,
}) => {
  const isMasked = !permissions.canSeeCustomer;

  if (isMasked) {
    return (
      <DetailsInfoRow
        iconName="male-avatar"
        isDisabled={ isDisabled }
        isExpandable
        isMasked
        withBorder
        title={
          <FormattedMessage
            id="Appointment.View.Details.Customer.Title"
            defaultMessage="** Customer"
          />
        }
      />
    );
  }

  return (
    <ExpandablePanel
      className={ theme.labelContainer }
      expandedAriaLabel={ intl.formatMessage({ id: 'Appointment.DetailsTab.CollapseCustomerButton.TitleAttribute', defaultMessage: '** Collapse customer details' }) }
      collapsedAriaLabel={ intl.formatMessage({ id: 'Appointment.DetailsTab.ExpandCustomerButton.TitleAttribute', defaultMessage: '** Expand customer details' }) }
      labelComponent={ () => (
        <div className={ theme.expandedPanel }>
          <DetailsInfoRow
            availability={ availability }
            isDisabled={ isDisabled }
            initials={ initials }
            avatar={ avatar }
            isExpandable
            title={
              <FormattedMessage
                id="Appointment.View.Details.Customer.Title"
                defaultMessage="** Customer"
              />
            }
            description={ name }
            isPatientInWaitingRoom={ isPatientInWaitingRoom }
          />
        </div>
      ) }
    >
      <div className={ theme.contentContainer }>
        <DetailsInfoRow
          insidePanel
          iconName={ gender.iconName }
          title={
            <FormattedMessage
              id="Appointment.View.Details.Customer.Gender.Title"
              defaultMessage="** Gender"
            />
          }
          description={ intl.formatMessage(gender.title) }
        />

        <If condition={ !isNil(policyId) }>
          <DetailsInfoRow
            insidePanel
            iconName="id-fill"
            title={
              <FormattedMessage
                id="Appointment.View.Details.Customer.ID.Title"
                defaultMessage="** ID"
              />
            }
            description={ policyId }
          />
        </If>

        <If condition={ dateOfBirth }>
          <DetailsInfoRow
            insidePanel
            iconName="cake"
            title={
              <FormattedMessage
                id="Appointment.View.Details.Customer.Age.Title"
                defaultMessage="** Age"
              />
            }
            description={ getAgeFormatted(dateOfBirth, intl) }
          />
        </If>

        <If condition={ phoneNumber }>
          <DetailsInfoRow
            insidePanel
            iconName="phone-fill"
            isPhoneNumber
            title={
              <FormattedMessage
                id="Appointment.View.Details.Customer.PoneNumber.Title"
                defaultMessage="** Phone number"
              />
            }
            description={ phoneNumber }
          />
        </If>

        <If condition={ email }>
          <DetailsInfoRow
            insidePanel
            iconName="envelope"
            title={
              <FormattedMessage
                id="Appointment.View.Details.Customer.Email.Title"
                defaultMessage="** Email"
              />
            }
            description={ email }
          />
        </If>
      </div>
    </ExpandablePanel>
  );
};

CustomerPatientInfoRow.propTypes = propTypes;
CustomerPatientInfoRow.defaultProps = defaultProps;

export default CustomerPatientInfoRow;
